import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const ConfirmContext = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return ctx;
}

function getConfirmButtonClass(variant) {
  switch (variant) {
    case 'danger':
      return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
    case 'warning':
      return 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500';
    case 'primary':
      return 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500';
    default:
      return 'bg-gray-900 hover:bg-gray-800 focus:ring-gray-700';
  }
}

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState({
    open: false,
    title: 'Confirm',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'danger',
    resolve: null,
    promptMode: false,
    promptLabel: '',
    promptDefault: '',
    required: false,
  });

  const [promptValue, setPromptValue] = useState('');
  const confirmButtonRef = useRef(null);
  const inputRef = useRef(null);

  const close = useCallback((result) => {
    setDialog((prev) => {
      if (typeof prev.resolve === 'function') {
        try {
          prev.resolve(result);
        } catch (_) {
          // ignore
        }
      }
      return {
        ...prev,
        open: false,
        resolve: null,
      };
    });
    setPromptValue('');
  }, []);

  const confirm = useCallback((options = {}) => {
    const {
      title = 'Confirm',
      message = '',
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      variant = 'danger',
      promptMode = false,
      promptLabel = '',
      promptDefault = '',
      required = false,
    } = options;

    return new Promise((resolve) => {
      setPromptValue(promptDefault);
      setDialog({
        open: true,
        title,
        message,
        confirmText,
        cancelText,
        variant,
        resolve,
        promptMode,
        promptLabel,
        promptDefault,
        required,
      });
    });
  }, []);

  useEffect(() => {
    if (!dialog.open) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(dialog.promptMode ? null : false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dialog.open, dialog.promptMode, close]);

  useEffect(() => {
    if (!dialog.open) return;
    const t = setTimeout(() => {
      if (dialog.promptMode) {
        inputRef.current?.focus?.();
      } else {
        confirmButtonRef.current?.focus?.();
      }
    }, 0);
    return () => clearTimeout(t);
  }, [dialog.open, dialog.promptMode]);

  const handleConfirm = useCallback(() => {
    if (dialog.promptMode) {
      if (dialog.required && !promptValue.trim()) return;
      close(promptValue.trim());
    } else {
      close(true);
    }
  }, [dialog.promptMode, dialog.required, promptValue, close]);

  const handleCancel = useCallback(() => {
    close(dialog.promptMode ? null : false);
  }, [dialog.promptMode, close]);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}

      {dialog.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50" role="presentation">
          <div
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-auto"
            role="dialog"
            aria-modal="true"
            aria-label={dialog.title}
          >
            <h3 className="text-xl font-bold text-gray-800 mb-4">{dialog.title}</h3>
            {typeof dialog.message === 'string' ? (
              <p className="text-gray-600 mb-6 whitespace-pre-line">{dialog.message}</p>
            ) : (
              <div className="text-gray-600 mb-6">{dialog.message}</div>
            )}

            {dialog.promptMode && (
              <div className="mb-6">
                {dialog.promptLabel && (
                  <label className="block text-sm font-medium text-gray-700 mb-1">{dialog.promptLabel}</label>
                )}
                <input
                  ref={inputRef}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={promptValue}
                  onChange={(e) => setPromptValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); } }}
                  placeholder={dialog.promptDefault || ''}
                />
                {dialog.required && !promptValue.trim() && (
                  <p className="text-xs text-red-500 mt-1">This field is required.</p>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                {dialog.cancelText}
              </button>
              <button
                ref={confirmButtonRef}
                type="button"
                onClick={handleConfirm}
                disabled={dialog.promptMode && dialog.required && !promptValue.trim()}
                className={`text-white px-4 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${getConfirmButtonClass(dialog.variant)}`}
              >
                {dialog.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
